/**
 *
 * @module checkResponseCode
 *
 */

import Check from 'check-types';
import Exception from './../exception/entry';
import Util from './entry';

const ErrorCode = Exception.ErrorCode;
const ErrorReason = Exception.ErrorReason;
const ExceptionReference = Exception.ExceptionReference;
const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const {
    object
} = Check.assert;

/**
 *
 * @access private
 * @param {Number} status
 * @param {Array} errors
 * @param {String} [methodName]
 * @note If the error case you are expecting is based on a method and status code but not error code(s) from the
 * service then you have to invoke `Util.checkResponseCode` as so `Util.checkResponseCode(status, errors, methodName)`.
 * Use the `methodName` below combined with a status code to create custom error logic so it doesn't fall into the
 * generic error handler in the else block
 * @returns {Object|undefined}
 *
 */
export function handleErrorCase(status, errors, methodName) {

    const error = Check.array(errors) ? errors[0] : {};
    const code = Util.getSafe(() => error.code);
    const description = Util.getSafe(() => error.description);
    const thumbnailMethods = ['getSpriteSheetThumbnails', 'getSpriteSheetThumbnail', 'getBifThumbnails'];
    const paymentMethods = ['getDefaultPaymentMethod', 'getPaymentCard'];

    let exceptionData;

    if (Check.nonEmptyString(methodName)) {

        if (Check.equal(status, 400) && Check.equal(methodName, 'submitOrderWithPaymentMethod')) {

            // InvalidDataException
            exceptionData = ExceptionReference.commerce.invalidData;

        } else if (Check.equal(status, 403) && Check.equal(methodName, 'lookupByZipCode')) {

            // InvalidZipCodeException
            exceptionData = ExceptionReference.commerce.invalidZipCode;

        } else if (Check.equal(status, 422) && Check.equal(methodName, 'createPaymentMethod')) {

            // InvalidPaymentInformationException
            exceptionData = ExceptionReference.commerce.invalidPaymentInformation;

        } else if (Check.equal(status, 404) && paymentMethods.includes(methodName)) {

            // PaymentMethodNotFoundException
            exceptionData = ExceptionReference.commerce.paymentMethodNotFound;

        } else if (Check.equal(status, 404) && thumbnailMethods.includes(methodName)) {

            // ThumbnailsNotAvailableException
            exceptionData = ExceptionReference.media.thumbnailsNotAvailable;

        } else if (Check.equal(status, 400) && Check.equal(methodName, 'check')) {

            if (Check.equal(code, ErrorCode.idpErrorPayloadFieldsIncorrect.code)) {

                // InvalidEmailSyntaxException
                exceptionData = ExceptionReference.bamIdentity.invalidEmailSyntax;

            }

        } else if (Check.less(status, 500) && Check.equal(methodName, 'changeEmail')) {

            if (Check.equal(code, ErrorCode.idpErrorPayloadFieldsIncorrect.code)) {

                // InvalidEmailException
                exceptionData = ExceptionReference.bamIdentity.invalidEmail;

            }

        } else if (Check.equal(status, 400) && Check.equal(methodName, 'query')) {

            // InvalidRequestException
            exceptionData = ExceptionReference.content.invalidRequest;

        }

    } else {

        /**
         *
         * @note Filters through the ErrorCode reference object to determine the error case based on
         * the supplied error code from the failed service request.
         *
         */
        for (const key of Object.keys(ErrorCode)) {

            const errorCode = ErrorCode[key];

            /**
             *
             * @note Some error cases require us to check against the returned error description.
             * Currently this is only used for LocationNotAllowedException and InvalidGrantException
             *
             */
            if (Check.array(errorCode.description)) {

                if (Check.equal(code, errorCode.code) && errorCode.description.includes(description)) {

                    exceptionData = errorCode.exceptionData;
                    break;

                }

            } else if (Check.equal(code, errorCode.code)) {

                exceptionData = errorCode.exceptionData;
                break;

            }

        }

    }

    return exceptionData;

}

/**
 *
 * @param {Number} status
 * @param {Array} errors
 * @returns {Array}
 *
 */
export function processReasons(status, errors) {

    const reasons = [];
    const errorCodes = [400, 401, 403, 404, 406, 409, 422, 429];

    if (errorCodes.includes(status) && Check.array(errors)) {

        errors.forEach((error) => {

            if (Util.getSafe(() => error.code)) {
                reasons.push(new ErrorReason(error.code, Util.getSafe(() => error.description)));
            }

        });

    }

    return reasons;

}

/**
 *
 * @param {Object} options
 * @param {Number} [status] - The HTTP status code
 * @param {String} [transactionId] - The identifier allows BAM to lookup the entire operation in DUST
 * @param {Array} [errors] - Group of errors
 * @param {String} [methodName] - name of the failed method
 * @desc Returns an exception based on the supplied parameters
 * @note Defaults exceptionData to an empty object if it is null or undefined. This will ensure a generic
 * ServiceException is created as a fallback.
 * @returns {ServiceException}
 *
 */
export function createException(options = {}) {

    const { status, transactionId, errors, methodName } = options;

    const reasons = processReasons(status, errors);

    let exceptionData = handleErrorCase(status, errors, methodName);

    if (!exceptionData) {

        if (status >= 500) {
            exceptionData = ExceptionReference.common.temporarilyUnavailable;
        } else {
            exceptionData = {};
        }

    }

    return new ServiceException({
        transactionId,
        reasons,
        exceptionData,
        status
    });

}

/**
 *
 * @param {Array} errors
 * @desc Checks for error cases thrown on status code 202
 * @returns {Boolean}
 *
 */
export function checkAcceptedCase(errors) {

    const codes = [ErrorCode.copyFailure.code, ErrorCode.copyLimitReached.code];

    if (Check.array(errors)) {

        return errors.some((error) => {

            const code = Util.getSafe(() => error.code);

            return codes.includes(code);

        });

    }

    return false;

}

/**
 *
 * @access private
 * @param {Object} response
 * @param {DustLogUtility} dustLogUtility
 * @param {String} [methodName] name of the failed method
 * @returns {Promise<Object>} resolves the response or rejects with an exception
 * @note methodName is used for error cases that rely on the http status and not a service error code.
 * it is currently only used for error cases relating to the Commerce feature.
 * @see https://tools.ietf.org/html/rfc6749#section-5.1 (success)
 * @see https://tools.ietf.org/html/rfc6749#section-5.2 (error)
 *
 */
export default function (response, dustLogUtility, methodName) {

    const errorMessage = {
        method: 'CheckResponseCode(response, dustLogUtility, dustEnabled)',
        get response() {
            return `${this.method} response should be an Object`;
        }
    };

    object(response, errorMessage.response, InvalidArgumentException);

    const { status, headers, data } = response;
    const { errors = [] } = data || {};
    const { error, error_description: errorDescription } = data;

    const isError = status >= 400;

    let isAcceptedErrorCase = false;
    let transactionId = '';

    /**
     * create consistent error object structure
     * service only returns error string
     */
    if (isError && Check.string(error)) {
        errors.push({ code: error, description: errorDescription });
    }

    if (headers && headers.get) {
        transactionId = headers.get('x-request-id') || '';
    }

    if (dustLogUtility) {
        dustLogUtility.parseResponse({ errors, response, transactionId });
    }

    // Check for error case on status code 202
    if (Check.equal(status, 202)) {
        isAcceptedErrorCase = checkAcceptedCase(errors);
    }

    if (isError || isAcceptedErrorCase) {
        return Promise.reject(createException({ status, transactionId, errors, methodName }));
    }

    return Promise.resolve(response);

}



// WEBPACK FOOTER //
// ./src/services/util/checkResponseCode.js